import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, FlaskConical, Dna, Zap, ArrowRight, Check, Shield } from 'lucide-react';
import apiClient from '../../lib/api';
import { canShowPopup, registerPopupShown, markPopupDismissed, wasPopupShown } from '../../lib/popupManager';

const PEPTIDE_OPTIONS = [
  'BPC-157', 'TB-500', 'GHK-Cu', 'CJC-1295 + Ipamorelin',
  'AOD-9604', 'Retatrutide', 'Semaglutide', 'Other',
];

export default function BloodTestPopup({ articleSlug }) {
  const popupId = `10x_blood_popup_${articleSlug}`;
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => wasPopupShown(popupId));
  const [formData, setFormData] = useState({ first_name: '', email: '', phone: '', interests: [] });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);
  const scrollTriggered = useRef(false);
  const exitTriggered = useRef(false);

  const show = useCallback(() => {
    if (dismissed || submitted) return;
    if (!canShowPopup(popupId)) return;
    setVisible(true);
    registerPopupShown(popupId);
  }, [dismissed, submitted, popupId]);

  const dismiss = () => {
    setVisible(false);
    setDismissed(true);
    markPopupDismissed(popupId);
  };

  // Trigger: 10 seconds
  useEffect(() => {
    timerRef.current = setTimeout(show, 10000);
    return () => clearTimeout(timerRef.current);
  }, [show]);

  // Trigger: 50% scroll
  useEffect(() => {
    const handler = () => {
      if (scrollTriggered.current) return;
      const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (pct > 0.5) {
        scrollTriggered.current = true;
        show();
      }
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, [show]);

  // Trigger: exit intent
  useEffect(() => {
    const handler = (e) => {
      if (exitTriggered.current) return;
      if (e.clientY < 5) {
        exitTriggered.current = true;
        show();
      }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [show]);

  const toggleInterest = (p) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(p)
        ? prev.interests.filter(i => i !== p)
        : [...prev.interests, p],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    setSubmitting(true);
    setError('');
    try {
      await apiClient.post('/leads', {
        ...formData,
        source: '10x-blood-test-popup',
      });
      setSubmitted(true);
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="blood-test-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-[#0e0e0e] border border-zinc-800 shadow-2xl shadow-[#00F0FF]/5 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="blood-test-popup-modal">
        {/* Close button */}
        <button onClick={dismiss} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-white transition z-10" data-testid="blood-test-popup-close">
          <X className="w-4 h-4" />
        </button>

        {!submitted ? (
          <>
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-zinc-800/50 bg-gradient-to-r from-[#00F0FF]/[0.04] to-transparent">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[9px] font-mono tracking-[0.15em] uppercase text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/20 px-2 py-0.5">Recommended</span>
                <span className="text-[9px] font-mono tracking-widest uppercase text-zinc-600">Partner</span>
              </div>
              <h3 className="font-heading text-xl sm:text-2xl text-white leading-tight" data-testid="blood-test-popup-title">
                GET YOUR BASELINE BEFORE<br />STARTING PEPTIDES
              </h3>
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                The 10X Health Blood Test is the gold standard for pre-protocol bloodwork. Know your hormones, inflammation markers, and metabolic health <em>before</em> you begin.
              </p>
            </div>

            {/* Benefits */}
            <div className="px-5 sm:px-6 pt-4 pb-2">
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { icon: FlaskConical, label: 'Full Panel Blood Test', sub: 'Hormones, metabolic, inflammation' },
                  { icon: Dna, label: '1:1 Provider Consult', sub: 'Licensed in all 50 states' },
                  { icon: Zap, label: 'Custom Protocols', sub: 'Personalized to your labs' },
                ].map((b, i) => (
                  <div key={b.label} className="text-center p-2">
                    <b.icon className="w-4 h-4 text-[#00F0FF] mx-auto mb-1" />
                    <p className="text-[10px] text-white font-semibold leading-tight">{b.label}</p>
                    <p className="text-[9px] text-zinc-600 leading-tight mt-0.5">{b.sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-5 sm:px-6 pb-5 space-y-3" data-testid="blood-test-form">
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="First Name" value={formData.first_name}
                  onChange={e => setFormData(p => ({ ...p, first_name: e.target.value }))}
                  className="bg-zinc-900 border border-zinc-800 text-white text-xs px-3 py-2.5 focus:border-[#00F0FF]/40 focus:outline-none transition placeholder:text-zinc-600"
                  data-testid="blood-test-name" />
                <input type="tel" placeholder="Phone" value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                  className="bg-zinc-900 border border-zinc-800 text-white text-xs px-3 py-2.5 focus:border-[#00F0FF]/40 focus:outline-none transition placeholder:text-zinc-600"
                  data-testid="blood-test-phone" />
              </div>
              <input type="email" placeholder="Email *" required value={formData.email}
                onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs px-3 py-2.5 focus:border-[#00F0FF]/40 focus:outline-none transition placeholder:text-zinc-600"
                data-testid="blood-test-email" />

              {/* Peptide interests */}
              <div>
                <p className="text-[9px] font-mono text-zinc-500 uppercase tracking-wider mb-1.5">Which peptides interest you?</p>
                <div className="flex flex-wrap gap-1.5">
                  {PEPTIDE_OPTIONS.map(p => (
                    <button type="button" key={p} onClick={() => toggleInterest(p)}
                      className={`px-2.5 py-1 text-[10px] font-mono border transition ${
                        formData.interests.includes(p)
                          ? 'bg-[#00F0FF]/10 border-[#00F0FF]/30 text-[#00F0FF]'
                          : 'bg-zinc-900/50 border-zinc-800 text-zinc-500 hover:border-zinc-600'
                      }`}
                      data-testid={`interest-pill-${p.replace(/[^a-zA-Z0-9]/g, '-')}`}>
                      {formData.interests.includes(p) && <Check className="w-2.5 h-2.5 inline mr-0.5" />}
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {error && <p className="text-red-400 text-xs">{error}</p>}

              <button type="submit" disabled={submitting}
                className="w-full py-3 bg-[#00F0FF] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#00d6e0] transition-colors disabled:opacity-50"
                data-testid="blood-test-submit">
                {submitting ? 'Submitting...' : 'Get My Blood Test Info'}
                {!submitting && <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />}
              </button>

              <p className="text-[8px] text-zinc-700 text-center flex items-center justify-center gap-1">
                <Shield className="w-2.5 h-2.5" /> Your information is secure. We never share your data.
              </p>
            </form>

            {/* Also from 10X */}
            <div className="px-5 sm:px-6 pb-4 border-t border-zinc-800/50 pt-3">
              <p className="text-[9px] font-mono text-zinc-600 uppercase tracking-wider mb-2">Also from 10X Health</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Genetic Test', sub: 'Decode your genes', href: 'https://10xhealthnetwork.com/pages/genetic-testing?utm_source=ninjathlete' },
                  { label: 'Precision Genetics', sub: 'Personalized nutrition', href: 'https://10xhealthnetwork.com/pages/precision-genetics?utm_source=ninjathlete' },
                  { label: 'Superhuman Protocol', sub: 'Magnetism + O2 + Light', href: 'https://10xhealthnetwork.com/pages/super-human-protocol?utm_source=ninjathlete' },
                ].map((p, i) => (
                  <a key={p.label} href={p.href} target="_blank" rel="nofollow sponsored noreferrer"
                    className="block p-2 bg-zinc-900/30 border border-zinc-800/50 hover:border-zinc-700 transition text-center">
                    <p className="text-[10px] text-white font-semibold">{p.label}</p>
                    <p className="text-[8px] text-zinc-600">{p.sub}</p>
                  </a>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* Success state */
          <div className="p-8 text-center" data-testid="blood-test-success">
            <div className="w-12 h-12 bg-[#00F0FF]/10 border border-[#00F0FF]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[#00F0FF]" />
            </div>
            <h3 className="font-heading text-2xl text-white mb-2">YOU'RE ALL SET</h3>
            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
              Your information has been saved. Click below to order your 10X Health Blood Test and get your baseline labs before starting your peptide protocol.
            </p>
            <a href="https://10xhealthsystem.com/blood-test/?utm_source=ninjathlete"
              target="_blank" rel="nofollow sponsored noreferrer"
              className="inline-block px-8 py-3 bg-[#00F0FF] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#00d6e0] transition-colors"
              data-testid="blood-test-order-link">
              Order 10X Blood Test <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
            </a>
            <button onClick={dismiss} className="block mx-auto mt-4 text-[10px] text-zinc-600 hover:text-zinc-400 transition">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
